/**
 * Thin client for /api/auth/me.
 *
 * Returns the signed-in user's plan tier + role from our tenants/users
 * table. Used by the task pane header to render the plan badge and by
 * feature-gating UI to decide whether to show upsell banners.
 *
 * Best-effort: if the call fails (network, 401 token expired, etc.) we
 * return null and the UI degrades gracefully (no badge, no upsell).
 */
import { getBootstrapToken } from './sso';

const API_BASE_URL = process.env.API_BASE_URL || 'http://localhost:3001';

export type PlanTier = 'tier1' | 'tier2' | 'tier3' | 'tier4';

/** UI-facing label for each plan tier. */
export const PLAN_LABEL: Record<PlanTier, string> = {
  tier1: 'Starter',
  tier2: 'Growth',
  tier3: 'Practice',
  tier4: 'Enterprise',
};

export interface AuthProfile {
  id: string;
  email?: string;
  displayName?: string;
  /** Postgres tenant UUID — distinct from Entra tenantId. */
  dbTenantId: string | null;
  plan: PlanTier | null;
  role: 'admin' | 'member' | null;
}

let cached: AuthProfile | null = null;
let inflight: Promise<AuthProfile | null> | null = null;

export async function fetchAuthProfile(): Promise<AuthProfile | null> {
  if (cached) return cached;
  if (inflight) return inflight;

  inflight = (async () => {
    try {
      const token = await getBootstrapToken();
      const resp = await fetch(`${API_BASE_URL}/api/auth/me`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
      });
      if (!resp.ok) return null;
      const body = (await resp.json()) as Partial<AuthProfile>;
      const profile: AuthProfile = {
        id: body.id ?? '',
        email: body.email,
        displayName: body.displayName,
        dbTenantId: body.dbTenantId ?? null,
        plan: (body.plan as PlanTier | null) ?? null,
        role: (body.role as 'admin' | 'member' | null) ?? null,
      };
      cached = profile;
      return profile;
    } catch {
      return null;
    } finally {
      inflight = null;
    }
  })();

  return inflight;
}

export function clearAuthProfileCache(): void {
  cached = null;
}
